import { LeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { Checkbox, Input, Spin, Tooltip } from "antd";
import { groupBy, uniq } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddMemberModal } from "../../../components/modal/AddMemberModal";
import { PreviewFileUploadModal } from "../../../components/modal/PreviewFileUploadModal";
import { conversationType } from "../../../config/Constant";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { diffHtml, extractContent } from "../../../utils/Utils";

import { IoMenu } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import {
  chatEvents$,
  SOCKET_STATUS,
  socketStatus$,
} from "../../../api/webSocketRx";
import { useDraftMailContext } from "../../../context/DraftMailContext";
import AddMemberList from "./AddMemberList";
import { MailComposer } from "./MailComposer";
import "./style.scss";

const RecipientSummary = () => {
  const { languageMap } = useInfoUser();
  const [isModalAddCC, setIsModalAddCC] = useState(false);
  const [isModalAddMember, setIsModalAddMember] = useState(false);

  const { memberOrgList, setMemberOrgList, cCOrgList, setCCOrgList } =
    useDraftMailContext();

  const memberGroupList = useMemo(() => {
    return Object.values(
      groupBy(
        memberOrgList?.filter((org) => org?.type === "PERSON" && org?.checked),
        "objectId",
      ),
    );
  }, [memberOrgList]);

  const ccGroupList = useMemo(() => {
    return Object.values(
      groupBy(
        cCOrgList?.filter((org) => org?.type === "PERSON" && org?.checked),
        "objectId",
      ),
    );
  }, [cCOrgList]);

  return (
    <div className="flex flex-col gap-[6px] mt-[3px] mb-[3px]">
      <div className="card-detail">
        <AddMemberList
          title={languageMap?.["chat.mail.recipient"] ?? "Recipient"}
          memberGroupList={memberGroupList}
          onClick={() => setIsModalAddMember(true)}
          orgList={memberOrgList}
          setOrgList={setMemberOrgList}
        />
      </div>
      <div className="card-detail">
        <AddMemberList
          title={languageMap?.["modal.notification.cc"] ?? "Cc"}
          memberGroupList={ccGroupList}
          onClick={() => setIsModalAddCC(true)}
          orgList={cCOrgList}
          setOrgList={setCCOrgList}
        />
      </div>
      {isModalAddMember && (
        <AddMemberModal
          isModalOpen={isModalAddMember}
          isModalClose={() => {
            setIsModalAddMember(false);
          }}
          screen={conversationType.CREATE_NOTIFICATION}
          orgList={memberOrgList}
          setOrgList={setMemberOrgList}
          isNonFetch={true}
        />
      )}
      {isModalAddCC && (
        <AddMemberModal
          isModalOpen={isModalAddCC}
          isModalClose={() => setIsModalAddCC(false)}
          screen={conversationType.CREATE_NOTIFICATION}
          orgList={cCOrgList}
          setOrgList={setCCOrgList}
          isNonFetch={true}
        />
      )}
    </div>
  );
};

const MailDraft = () => {
  const { languageMap } = useInfoUser();
  const navigate = useNavigate();

  const {
    onBackToConversations,
    selectedConversation,
    setIsMenu,
    setIsDiffHtmlMail,
    setKeydownMenuOn,
  } = useMenuContext();

  const {
    setTotalNotificationItem,
    setIsReply,
    title,
    setTitle,
    cCOrgList,
    memberOrgList,
    isLoading,
    isNotification,
    setIsNotification,
    draftMessage,
  } = useDraftMailContext();

  const notificationRepliesRef = useRef(null);
  const bottomMessagesRef = useRef(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [mailContent, setMailContent] = useState({
    content: "",
    oldContent: "",
  });
  const [oldFiles, setOldFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [isSendingSuccess, setIsSendingSuccess] = useState(false);

  const handleCancelUpload = () => {
    setIsOpenUploadModal(false);
    setUploadFiles([]);
  };

  const addMemberOrg = () => {
    let userIds = memberOrgList
      ?.filter((org) => org?.type === "PERSON" && org?.checked)
      ?.map((org) => org?.objectId);
    userIds = uniq(userIds);

    let ccIds = cCOrgList
      ?.filter((org) => org?.type === "PERSON" && org?.checked)
      ?.map((org) => org?.objectId);
    ccIds = uniq(ccIds);

    return { userIds, ccIds };
  };

  useEffect(() => {
    const { text, images } = extractContent(mailContent?.content || "");

    const { images: oldImages } = extractContent(mailContent?.oldContent || "");

    if (selectedConversation?.type === conversationType.DRAFT) {
      const diffs = diffHtml(mailContent?.oldContent, mailContent?.content);
      const changedText = diffs?.filter(
        (diff) => diff[0] === 1 || diff[0] === -1,
      );
      const addedImages = images?.filter((img) => !oldImages.includes(img));
      const removedImages = oldImages?.filter((img) => !images.includes(img));

      let newFiles = [];
      let removedFiles = [];

      if (oldFiles?.length > 0) {
        newFiles = previewFiles?.filter(
          (file) =>
            !oldFiles?.some(
              (oldFile) => oldFile?.resourceId === file?.resourceId,
            ),
        );

        removedFiles = oldFiles?.filter(
          (oldFile) =>
            !previewFiles?.some(
              (file) => file?.resourceId === oldFile?.resourceId,
            ),
        );
      }

      if (
        changedText?.length > 0 ||
        addedImages?.length > 0 ||
        removedImages?.length > 0 ||
        newFiles?.length > 0 ||
        removedFiles?.length > 0
      ) {
        setIsDiffHtmlMail(true);
      } else setIsDiffHtmlMail(false);
    } else if (!selectedConversation) {
      if (
        text?.trim()?.length > 0 ||
        images?.length > 0 ||
        previewFiles?.length > 0
      ) {
        setIsDiffHtmlMail(true);
      } else setIsDiffHtmlMail(false);
    }
  }, [mailContent?.oldContent, mailContent?.content]);

  useEffect(() => {
    if (bottomMessagesRef.current && isSendingSuccess) {
      bottomMessagesRef.current.scrollIntoView({ behavior: "instant" });
      setIsSendingSuccess(false);
    }
  }, [isSendingSuccess]);

  useEffect(() => {
    setTitle(selectedConversation?.type ? selectedConversation?.name : "");
    setUploadFiles([]);
    setPreviewFiles([]);
    setIsReply(false);
    setOldFiles([]);
    setMailContent({ content: "", oldContent: "" });
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type === conversationType.DRAFT) return;

      const mail = eventData?.mail || eventData?.message;
      const mailConversationId =
        mail?.conversationId || eventData?.seenMessageTs?.conversationId;

      if (
        mailConversationId &&
        mailConversationId !== selectedConversation?.conversationId
      ) {
        return;
      }

      navigate(`/conversation/new-mail/${mailConversationId}`);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const prevStatusRef = { current: null };

    const sub = socketStatus$.subscribe((s) => {
      if (s?.scope !== "chat") return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type !== conversationType.NOTIFICATION) return;

      const prev = prevStatusRef.current;
      prevStatusRef.current = s.status;

      // vừa reconnect xong -> refetch
      if (
        prev !== SOCKET_STATUS.CONNECTED &&
        s.status === SOCKET_STATUS.CONNECTED
      ) {
        const controller = new AbortController();
        // fetchTopMessages(controller.signal);
        // không cần cleanup ở đây vì request nhỏ; nếu bạn muốn strict:
        // return () => controller.abort();
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  return (
    <div className="flex overflow-hidden flex-1">
      <div className="chat-content h-[100vh]">
        <div className="header sticky top-0 z-50 bg-white !flex !flex-col">
          <div className="flex items-center justify-between header-chat border-b border-gray-300">
            <div className="left ">
              <button onClick={onBackToConversations}>
                <LeftOutlined />
              </button>

              <div className="flex flex-col me-40 w-full ">
                <Input
                  className="border-none text-lg font-semibold focus:ring-0 focus:border-none"
                  placeholder={
                    languageMap?.["modal.notification.title"] ??
                    "Title(not blank)"
                  }
                  value={title}
                  onChange={(e) => {
                    setTitle(e?.target?.value);
                  }}
                  maxLength={255}
                  onFocus={() => setKeydownMenuOn(false)}
                  onBlur={() => setKeydownMenuOn(true)}
                />
              </div>
            </div>
            <button
              className="icon menu-icon"
              onClick={() => {
                setIsMenu(true);
              }}
            >
              <IoMenu size={25} />
            </button>
            <div className="right">
              <Tooltip
                placement="bottom"
                title={
                  languageMap?.["modal.notification.notice"] ??
                  "When you want to notify to a lot of people"
                }
                color={"#0091ff"}
              >
                <Checkbox
                  checked={isNotification}
                  onChange={() => setIsNotification(!isNotification)}
                  className="font-semibold"
                >
                  {languageMap?.["notification.notice"] ?? "Notice"}
                </Checkbox>
              </Tooltip>
            </div>
          </div>
          <RecipientSummary />
        </div>
        <div
          className="chat-body w-full h-[100%] bg-[white] overflow-y-auto overflow-y-scroll-email px-[10px] mr-[5px]"
          ref={notificationRepliesRef}
        >
          <MailComposer
            notification={draftMessage}
            addMemberOrg={addMemberOrg}
          />
        </div>
        {isLoading && (
          <div className="relative bottom-[50%] left-[50%]">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 35 }} spin />}
            />
          </div>
        )}
      </div>
      {isOpenUploadModal && (
        <PreviewFileUploadModal
          isModalOpen={isOpenUploadModal}
          handleCancel={handleCancelUpload}
          uploadFiles={uploadFiles}
          setUploadFiles={setUploadFiles}
          previewFiles={previewFiles}
          setPreviewFiles={setPreviewFiles}
        />
      )}
    </div>
  );
};

export { MailDraft, RecipientSummary };
